import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['controls', 'tableRow', 'listButton'];
  static values = {
    perPage: { type: Number, default: 10 },
    // [left, left_gap, right_gap, right]
    size: { type: Array, default: [1, 2, 2, 1] }
  };

  connect() {
    this.currentPage = 1;
    this.totalPages = 0;
  }

  tableRowTargetConnected(element) {
    this.#updatePagination();
  }

  goToPage(event) {
    const page = parseInt(event.params.page);
    this.currentPage = page;
    this.#refreshDisplay();
  }

  #updatePagination() {
    const rows = this.tableRowTargets;
    this.totalPages = Math.ceil(rows.length / this.perPageValue);

    this.#refreshDisplay();
  }

  #refreshDisplay() {
    this.tableRowTargets.forEach((row, index) => {
      const page = Math.floor(index / this.perPageValue) + 1;
      row.style.display = page === this.currentPage ? '' : 'none';
    });

    this.#buildPaginationButtons();

    this.controlsTarget.classList.toggle('d-none', this.totalPages <= 1);
  }

  #buildPaginationButtons() {
    this.listButtonTarget.innerHTML = '';

    if (this.totalPages <= 1) return;

    const pages = this.#calculateVisiblePages();

    pages.forEach((item) => {
      if (item === 'gap') {
        this.#createGap();
      } else {
        this.#createPageButton(item);
      }
    });
  }

  #calculateVisiblePages() {
    const [left, leftGap, rightGap, right] = this.sizeValue;
    const current = this.currentPage;
    const total = this.totalPages;

    const maxVisiblePages = left + leftGap + 1 + rightGap + right;
    if (total <= maxVisiblePages) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages = new Set([
      ...Array.from({ length: left }, (_, i) => i + 1),
      ...Array.from({ length: right }, (_, i) => total - right + i + 1),
      ...Array.from({ length: leftGap + rightGap + 1 }, (_, i) => current - leftGap + i).filter(p => p > 0 && p <= total)
    ]);

    const sorted = [...pages].sort((a, b) => a - b);
    return sorted.flatMap((page, i) => this.#insertGapOrMissingPage(page, i, sorted));
  }

  #insertGapOrMissingPage(page, index, sortedPages) {
    if (index === 0) return [page];

    const gap = page - sortedPages[index - 1];

    if (gap === 2) {
      return [sortedPages[index - 1] + 1, page];
    } else if (gap > 2) {
      return ['gap', page];
    }

    return [page];
  }

  #createPageButton(pageNumber) {
    const li = document.createElement('li');
    li.classList.add('page-item');

    const button = document.createElement('button');
    button.textContent = pageNumber;
    button.classList.add('page-link');
    button.dataset.action = 'click->table-pagination#goToPage';
    button.dataset.tablePaginationPageParam = pageNumber;

    if (pageNumber === this.currentPage) {
      button.classList.add('active');
      button.setAttribute('aria-current', 'page');
    }

    li.appendChild(button);
    this.listButtonTarget.appendChild(li);
  }

  #createGap() {
    const li = document.createElement('li');
    li.classList.add('page-item', 'disabled');

    const span = document.createElement('span');
    span.classList.add('page-link');
    span.textContent = '...';
    span.setAttribute('aria-hidden', 'true');

    li.appendChild(span);
    this.listButtonTarget.appendChild(li);
  }
}
